import { DOCUMENT } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  afterNextRender,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AuthService } from '../../../core/auth/auth.service';
import { TranslationService } from '../../../core/i18n/translation.service';
import {
  IssueDto,
  IssueLocationDto,
  IssuePriority,
  IssueRoleDto,
} from '../models/issue.models';
import { IssuesApi } from '../services/issues.api';

const PRIORITY_DUE_HOURS: Record<IssuePriority, number> = {
  low: 8,
  medium: 4,
  critical: 1,
};

@Component({
  selector: 'app-issue-report-modal',
  imports: [ReactiveFormsModule],
  templateUrl: './issue-report-modal.component.html',
  styleUrl: './issue-report-modal.component.css',
})
export class IssueReportModalComponent implements OnInit, OnDestroy {
  private readonly issuesApi = inject(IssuesApi);
  private readonly authService = inject(AuthService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly titleInput = viewChild<ElementRef<HTMLInputElement>>('titleInput');
  private readonly dialogPanel = viewChild<ElementRef<HTMLElement>>('dialogPanel');
  private readonly previousBodyOverflow = this.document.body.style.overflow;

  protected readonly i18n = inject(TranslationService);
  readonly closed = output<void>();
  readonly reported = output<IssueDto>();

  protected readonly form = this.formBuilder.nonNullable.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    description: ['', Validators.required],
    priority: ['medium' as IssuePriority, Validators.required],
    dueDate: ['', Validators.required],
    locationId: ['', Validators.required],
    assignedRoleId: ['', Validators.required],
  });
  protected readonly locations = signal<IssueLocationDto[]>([]);
  protected readonly roles = signal<IssueRoleDto[]>([]);
  protected readonly attachments = signal<File[]>([]);
  protected readonly isLoadingOptions = signal(true);
  protected readonly isSubmitting = signal(false);
  protected readonly optionsLoadError = signal('');
  protected readonly attachmentError = signal('');
  protected readonly submitError = signal('');
  protected readonly hasLocations = computed(() => this.locations().length > 0);
  protected readonly hasRoles = computed(() => this.roles().length > 0);
  protected readonly priorities: Array<{ value: IssuePriority; labelKey: string }> = [
    { value: 'low', labelKey: 'issueReport.priority.low' },
    { value: 'medium', labelKey: 'issueReport.priority.medium' },
    { value: 'critical', labelKey: 'issueReport.priority.critical' },
  ];

  constructor() {
    this.document.body.style.overflow = 'hidden';
    this.setDueDateForPriority(this.form.controls.priority.value);
    this.form.controls.priority.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(priority => this.setDueDateForPriority(priority));
    afterNextRender(() => this.titleInput()?.nativeElement.focus({ preventScroll: true }));
  }

  ngOnInit(): void {
    forkJoin({
      locations: this.issuesApi.getLocations(),
      roles: this.issuesApi.getRoles(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ locations, roles }) => {
          this.locations.set(locations.results.filter(({ status }) => status === 'active'));
          this.roles.set(roles.results);
          this.isLoadingOptions.set(false);
        },
        error: () => {
          this.optionsLoadError.set(this.i18n.t('issueReport.errors.loadOptions'));
          this.isLoadingOptions.set(false);
        },
      });
  }

  ngOnDestroy(): void {
    this.document.body.style.overflow = this.previousBodyOverflow;
  }

  @HostListener('document:keydown', ['$event'])
  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const panel = this.dialogPanel()?.nativeElement;
    if (!panel) {
      return;
    }

    const focusableElements = Array.from(
      panel.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      ),
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements.at(-1);
    if (!firstElement || !lastElement) {
      event.preventDefault();
      return;
    }

    const activeElement = this.document.activeElement;
    if (event.shiftKey && (activeElement === firstElement || !panel.contains(activeElement))) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  protected close(): void {
    if (!this.isSubmitting()) {
      this.closed.emit();
    }
  }

  protected addAttachments(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFiles = Array.from(input.files ?? []);
    input.value = '';

    const images = selectedFiles.filter(({ type }) => type.startsWith('image/'));
    if (images.length !== selectedFiles.length) {
      this.attachmentError.set(this.i18n.t('issueReport.errors.invalidAttachment'));
    } else {
      this.attachmentError.set('');
    }

    if (images.length) {
      this.attachments.update((current) => [...current, ...images]);
    }
  }

  protected removeAttachment(index: number): void {
    this.attachments.update((current) => current.filter((_, itemIndex) => itemIndex !== index));
    this.attachmentError.set('');
  }

  protected priorityDueHint(priority: IssuePriority): string {
    return this.i18n.t(`issueReport.priorityDueHint.${priority}`);
  }

  private setDueDateForPriority(priority: IssuePriority): void {
    const dueDate = new Date(Date.now() + PRIORITY_DUE_HOURS[priority] * 60 * 60 * 1000);
    this.form.controls.dueDate.setValue(formatLocalDateTime(dueDate), { emitEvent: false });
  }

  protected async submit(): Promise<void> {
    this.form.markAllAsTouched();
    this.submitError.set('');

    const value = this.form.getRawValue();
    if (!value.title.trim()) {
      this.form.controls.title.setErrors({ required: true });
    }
    if (!value.description.trim()) {
      this.form.controls.description.setErrors({ required: true });
    }

    if (
      this.form.invalid ||
      this.isLoadingOptions() ||
      !this.hasLocations() ||
      !this.hasRoles()
    ) {
      return;
    }

    const assignedRoleId = Number(value.assignedRoleId);
    if (!Number.isInteger(assignedRoleId) || assignedRoleId < 1) {
      return;
    }

    this.isSubmitting.set(true);
    try {
      const hasValidSession = await this.authService.ensureValidSession();
      if (!hasValidSession) {
        this.submitError.set(this.i18n.t('issueReport.errors.sessionExpired'));
        return;
      }

      const issue = await firstValueFrom(
        this.issuesApi.createIssue({
          title: value.title.trim(),
          description: value.description.trim(),
          priority: value.priority,
          dueDate: new Date(value.dueDate).toISOString(),
          locationId: value.locationId,
          assignedRoleId,
          attachments: this.attachments(),
        }),
      );
      this.reported.emit(issue);
    } catch (error) {
      const errorKey =
        error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)
          ? 'issueReport.errors.sessionExpired'
          : 'issueReport.errors.submit';
      this.submitError.set(this.i18n.t(errorKey));
    } finally {
      this.isSubmitting.set(false);
    }
  }
}

function formatLocalDateTime(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return [
    date.getFullYear(),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes()),
  ].join('');
}
